import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { BUSINESS } from "../data/site";
import { IMAGES } from "../data/services";

const PRINCIPLES = [
  { title: "Repair first, always", text: "We make our living fixing things, not upselling replacements. If a $200 weld beats a $4,000 gate, you hear it from us first." },
  { title: "The price is the price", text: "Flat, written, approved before we strike an arc. We don't bill by the hour because slow welding shouldn't be your problem." },
  { title: "Show up like a professional", text: "Stocked rig, clean welds, and we walk you through the finished repair before we leave. Every job, warranty in writing." },
];

const About = () => (
  <main data-testid="about-page">
    <SEO
      title={`About Aledo Welding | Repair-First Mobile Welder Since ${BUSINESS.established}`}
      description="Aledo Welding is a repair-first mobile welding company serving Tarrant & Parker County since 2024. Licensed, insured, and built on straight prices."
    />
    <PageHero
      kicker={`Established ${BUSINESS.established} · Fort Worth, TX`}
      title="A repair shop with wheels, not a fab shop with a waitlist"
      lede="We started Aledo Welding because the broken stuff never happens near a welding shop. So we put the shop on a truck."
      testidPrefix="about"
    />
    <section className="bg-paper pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 space-y-5 text-smoke leading-relaxed" data-testid="about-story">
          <Reveal>
            <p>
              Most welding shops are built around custom fabrication — big projects, long lead times, and little interest in your sagging driveway gate. We went the other way. {BUSINESS.name} exists for the repair call: the gate that won't close, the trailer that cracked on 820, the excavator bucket that split on a Friday afternoon.
            </p>
            <p>
              Since {BUSINESS.established} we've run Tarrant and Parker County with a simple model: you send photos, we send a straight price, and the rig shows up where the problem is. Homeowners get plain-language answers. Contractors get qualified welders and a repair they can trust.
            </p>
            <p>
              And when something genuinely isn't worth repairing, we say so — and only then do we talk about building new. That order matters. It's why most of our work comes from referrals and repeat commercial accounts.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-rust pointer-events-none" aria-hidden />
            <img src={IMAGES.emergency} alt="Welder at work on an on-site repair in Fort Worth" data-testid="about-image" className="relative w-full aspect-[4/3] object-cover" loading="lazy" />
          </div>
        </div>
      </div>
    </section>
    <section className="bg-cream border-t border-warmline py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl mb-10" data-testid="about-principles-heading">How we run every job</h2>
        <div className="grid md:grid-cols-3 gap-px bg-warmline border border-warmline" data-testid="about-principles">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="bg-cream p-7 sm:p-9 h-full">
                <span className="font-display font-black text-4xl text-ink/10">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display font-extrabold tracking-tight text-xl mt-4">{p.title}</h3>
                <p className="mt-3 text-smoke text-[15px] leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="text-smoke">
            Want the full picture? <Link to="/capabilities" data-testid="about-capabilities-link" className="text-rust font-semibold hover:text-rust-dark">See our capabilities & certifications</Link> or <Link to="/contact" data-testid="about-contact-link" className="text-rust font-semibold hover:text-rust-dark">get a quote</Link>.
          </p>
        </Reveal>
      </div>
    </section>
  </main>
);

export default About;
